import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";
import { type DistanceUnit, LandXML, distanceUnits } from "earthbrain-viewer";

interface IGeometryLike {
	vertices: number[];
	indices: number[];
}

/**
 * Converts a TIN geometry into a LandXML string.
 * For ft/usfeet the Units element becomes Imperial and coordinates are scaled
 * into that unit.
 */
export function buildLandXmlString(
	geometry: IGeometryLike,
	projectName: string,
	objectName = "3DFace",
	distanceUnit: DistanceUnit = distanceUnits.METERS,
): string {
	// Distance conversion is a pure factor, so the display value of 1 m is the factor.
	const scale = createDistanceUnitPort(distanceUnit).toDisplay(1);
	// LandXML point order is Y X Z (northing easting elevation), so swap X/Y
	// from the local (x, y, z) buffer — same convention as the solid-mesh and
	// road LandXML exports. LandXML.exportMesh writes the buffer verbatim.
	const swappedVertices: number[] = [];
	for (let i = 0; i < geometry.vertices.length; i += 3) {
		swappedVertices.push(
			geometry.vertices[i + 1] * scale,
			geometry.vertices[i] * scale,
			geometry.vertices[i + 2] * scale,
		);
	}
	const xml = LandXML.exportMesh(
		swappedVertices,
		geometry.indices,
		projectName,
		undefined,
		objectName,
		distanceUnit,
	);
	if (!xml) {
		throw new Error(`Failed to build LandXML for ${projectName}`);
	}
	return xml;
}
